/**
 * Tiny RFC-4180-ish CSV parser. Handles quoted fields, escaped quotes ("")
 * inside quoted fields, comma + newline separators, and LF/CRLF line endings.
 *
 * Returns an array of row objects keyed by the header row. Empty cells become
 * undefined (not empty string) so the backend coercer can spot "missing" vs
 * "explicitly empty".
 *
 * NOT a full CSV implementation — no semicolon delimiters, no BOM detection,
 * no streaming. Adequate for typical Excel "Save as CSV" exports.
 */
export function parseCsvToRows(text: string): { headers: string[]; rows: Array<Record<string, string | undefined>> } {
  const cells = parseCells(text);
  if (cells.length === 0) return { headers: [], rows: [] };

  const headers = cells[0].map((h) => h.trim());
  const rows: Array<Record<string, string | undefined>> = [];

  for (let i = 1; i < cells.length; i++) {
    const row = cells[i];
    // Skip totally-empty trailing rows
    if (row.every((c) => c === '')) continue;
    const obj: Record<string, string | undefined> = {};
    headers.forEach((h, j) => {
      const v = (row[j] ?? '').trim();
      obj[h] = v === '' ? undefined : v;
    });
    rows.push(obj);
  }

  return { headers, rows };
}

function parseCells(text: string): string[][] {
  const out: string[][] = [];
  let current: string[] = [];
  let cell = '';
  let inQuotes = false;
  let i = 0;

  while (i < text.length) {
    const c = text[i];

    if (inQuotes) {
      if (c === '"') {
        if (text[i + 1] === '"') { cell += '"'; i += 2; continue; }
        inQuotes = false; i += 1; continue;
      }
      cell += c; i += 1; continue;
    }

    if (c === '"') { inQuotes = true; i += 1; continue; }
    if (c === ',') { current.push(cell); cell = ''; i += 1; continue; }
    if (c === '\r') {
      if (text[i + 1] === '\n') i += 1;
      current.push(cell); out.push(current);
      current = []; cell = ''; i += 1; continue;
    }
    if (c === '\n') {
      current.push(cell); out.push(current);
      current = []; cell = ''; i += 1; continue;
    }

    cell += c; i += 1;
  }

  // Trailing cell / row
  if (cell.length > 0 || current.length > 0) {
    current.push(cell);
    out.push(current);
  }

  return out;
}
